import { Injectable } from '@angular/core';
import { MsalService, BroadcastService } from '@azure/msal-angular';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, forkJoin } from 'rxjs';
import { AzureadHelperService } from '../azuread/azuread-helper.service';
import { PromptState } from 'msal/lib-commonjs/utils/Constants';
import { Router } from '@angular/router';
import { WizardStep } from '../shared/wizard.service';
import { ConfigLoaderService } from '../shared/config-loader.service';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type':  'application/json',
    "Accept": "application/json"
  })
};

@Injectable({
  providedIn: 'root'
})
export class AzureFunctionsService {

  

  constructor(
    private authService: MsalService, 
    private adHelper: AzureadHelperService,
    private http: HttpClient,
    private router: Router,
    private broadcastService: BroadcastService,
    private configService: ConfigLoaderService) { }
  
  private buildFunctionUrl(functionName: string){
    return this.configService.Configuration.azFunctions.baseUrl + "/api/" + functionName
  }
  
  // authentication served by the MSAL, don't forget to add functions's root URL as protected resource
  executePostFunction<T>(functionName: string, body: any, params? : HttpParams | {[param: string] : string}): Observable<T>{
    return this.http.post<T>(this.buildFunctionUrl(functionName), body, { params: params});
  }

  executeGetFunction<T>(functionName: string, params? : {[param: string] : string}): Observable<T>{
    return this.http.get<T>(this.buildFunctionUrl(functionName), { params: params});
  }

  status(){
    return this.executeGetFunction<Status>("Status").toPromise();
  }

  runConsensts(){
    return this.executeGetFunction<ApplicationConsentModel[]>("GetApplicationsForConsent?settingsFilePath=settings/ApplicationsToConsent.json").toPromise().then(async (apps) => {
      var allPromises = [];
      for(let i=0;i<apps.length;i++){
          await this.adHelper.loginAdminConsenstPopup(apps[i].applicationId);
        };

      //return Promise.all(allPromises);
    });
  }

  subcriptions(){
    return this.executeGetFunction<Subscription[]>("GetSubscriptions").toPromise();
  }

  startUsersCreation(processId: string){
      return this.executePostFunction("CreateUsers", 
      {}, 
      {
        "settingsFilePath": "settings/Interactive_InitialSetup_DefaultUsers.json",
        "processId": processId
      }).toPromise();
  }

  checkPermissions(){
    return new Promise((resolve, reject) => this.executeGetFunction<boolean>("IsUserSubscriptionOwner").toPromise().then((r) => r ? resolve() : reject("You not an owner or administrator of any subscription")).catch((e) => reject(e))) ;
  }

  startGroupsCreation(processId: string){
    return this.executePostFunction("CreateGroups", 
    {},
    {
      "settingsFilePath": "settings/Interactive_InitialSetup_DefaultGroups.json",
      "processId": processId
    }).toPromise();
  }

  registerResourceProviders(subscription: Subscription, processId: string){
    return this.executePostFunction("RegisterResourceProviders",
    {},
    {
      "settingsFilePath": "settings/Interactive_InitialSetup_ResourceProviders.json",
      "subscriptionId": subscription.subscriptionId,
      "processId": processId
    }).toPromise();
  }

  assignDirectoryRoles(processId: string){
    return this.executePostFunction("AssignDirectoryRoles",
     {}, 
    {
      "settingsFilePath": "settings/Interactive_InitialSetup_DefaultUsers.json",
      "processId": processId
    }).toPromise();
  }


  assignSubscriptionRoles(subscription: Subscription, processId: string){
    return this.executePostFunction("AssignSubscriptionRoles", {},
    {
      "settingsFilePath": "settings/Interactive_InitialSetup_SubscriptionRoles.json",
      "subscriptionId": subscription.subscriptionId,
      "processId": processId
    }).toPromise();
  }

  createResourceGroups(subscription: Subscription, processId: string){
    return this.executePostFunction("CreateResourceGroups", 
    {},
    {
      "settingsFilePath": "settings/Interactive_InitialSetup_AzureResourceGroups.json",
      "subscriptionId": subscription.subscriptionId,
      "processId": processId
    }).toPromise();
  }

  assignEntAppOwner(subscription: Subscription, processId: string){
    return this.executePostFunction("AssignEntAppOwner", 
    {},
    {
      "settingsFilePath": "settings/Interactive_InitialSetup_DefaultUsers.json",
      "subscriptionId": subscription.subscriptionId,
      "processId": processId
    }).toPromise();
    
  }

  availableProcesses(){
    return this.executeGetFunction<IProcessModel[]>("GetAllowedProcesses").toPromise();
  }

  submitOnboardingRequest(email: string){
    return this.executePostFunction("SubmitOnboardingRequest", {email: email}).toPromise();
  }

  startOffboardProcess(editions: string[]){

    var p = new HttpParams();
    for (let i = 0; i < editions.length; i++) {
      const edition = editions[i];
      p = p.append("editions", edition);
    }

    return this.executePostFunction("StartOffboardProcess", {}, p).toPromise();
  }

  getOnboardingProcess(): Promise<OnboardingProcessModel>{
    return this.executeGetFunction<OnboardingProcessModel>("GetOnboardingProcess").toPromise();
  }

  getOffboardingStatus(): Promise<string>{
    return this.executeGetFunction<string>("GetOffboardingStatus").toPromise();
  }

  getOffboardingProcess(): Promise<OnboardingProcessModel>{
    return this.executeGetFunction<OnboardingProcessModel>("GetOffboardingProcess").toPromise();
  }

  getProcessHistory(): Promise<IProcess[]>{
    return this.executeGetFunction<IProcess[]>("GetProcessHistory").toPromise();
  }

  downloadProcessHistory(processId: string): Promise<Blob>{
    return  this.http.get(this.buildFunctionUrl("DownloadProcessHistory"), { params: {"processId": processId}, responseType: 'blob'}).toPromise();
  }

  testFileMerging(settingsFilePath: string, objectType: string, editions: string[], domain: string): Promise<any>{
    return  this.http.get(this.buildFunctionUrl("TestFileMerging"), { params: {"settingsFilePath": settingsFilePath, "objectType": objectType, "editions": editions, "domain": domain}}).toPromise();
  }
}

export class Status{
  user: string;
  isAuthenticated: boolean;
  authType: string;
}

export class Subscription{
  subscriptionId: string;
  displayName: string;
}

export class OnboardingProcessModel{
  subscription: Subscription;
  process: IProcess;
  appConsentRequired: boolean;
  editions: string[];
}

class ApplicationConsentModel{
  applicationId: string;
  redirectUrl: string;
  scopes: string[];
}

export enum ProcessStatusEnum{
  NotStarted, Running, Completed, Failed
}

export const ProcessStatusEnumNames = new Map<number, string>([
  [ProcessStatusEnum.NotStarted, 'Not Started'],
  [ProcessStatusEnum.Running, 'Running'],
  [ProcessStatusEnum.Completed, 'Completed'],
  [ProcessStatusEnum.Failed, 'Failed']
]);

export interface IProcessModel{
  linkRef: string;
  title: string;
}

export interface IProcess{
  processName: string;
  processId?: string;
  endTime?: Date;
  status: ProcessStatusEnum;
  steps: WizardStep[];
  startTime?: Date;
  behalfOf?: string;
}

export interface IProcessStep{
  
  title: string;
  visible: boolean;
  uiFunctionName: string;
  status: ProcessStatusEnum
  statusDescription: string;
  selected: boolean;
  disabled: boolean;
}